import { api, type DirectivesContext } from '../api';
import { callout, field, input, opt, openDialog, select, val, numVal } from '../dialog';
import { esc, fmtHours, html } from '../ui';

/**
 * Recording an airworthiness directive, service bulletin or standard change.
 *
 * Until this existed an organisation could not have any directives at all — the
 * ones in the demo fleet came from the seed — so the AD status column, the
 * dashboard's compliance figure and the airworthiness review's directive check
 * all had nothing to work on.
 *
 * Two decisions are asked for rather than assumed. Whether it is *mandatory*
 * follows from what it is: an AD and an airworthiness limitation must be
 * complied with, a service bulletin need not be unless an AD or the approved
 * programme makes it so. And whether it *applies to this aircraft* is a
 * judgement someone makes by reading the effectivity — recorded with the person
 * who made it and why, which is what an auditor asks to see.
 */

const AUTHORITIES = [
  ['EASA', 'EASA'],
  ['FAA', 'FAA'],
  ['AACR', 'National authority (AACR)'],
  ['TCCA', 'Transport Canada'],
  ['TCH', 'Type certificate holder'],
] as const;

const KINDS = [
  ['AD', 'Airworthiness directive'],
  ['SB', 'Service bulletin'],
  ['SL', 'Service letter'],
  ['CS_STAN', 'Standard change / standard repair (CS-STAN)'],
  ['AIRWORTHINESS_LIMITATION', 'Airworthiness limitation (ALS)'],
] as const;

/** What recording this kind actually commits the organisation to. */
function kindNote(kind: string): string {
  switch (kind) {
    case 'AD':
      return callout('caution', '!', html`
        An airworthiness directive is <b>mandatory</b>. Until it is complied with, the aircraft does not
        meet its type design and no airworthiness review can conclude in its favour. A recurring one
        becomes a task in the maintenance programme so it reaches the due list.
      `);
    case 'AIRWORTHINESS_LIMITATION':
      return callout('caution', '!', html`
        Airworthiness limitations are <b>mandatory</b> and the maintenance programme must reflect them —
        ML.A.302(c)(1). Record the limitation here and check the programme carries the task.
      `);
    case 'SB':
      return callout('info', 'i', html`
        A service bulletin is <b>not mandatory in itself</b>. It becomes mandatory only when an
        airworthiness directive calls it up, or when the approved maintenance programme adopts it.
        Recording it here tracks it either way.
      `);
    case 'CS_STAN':
      return callout('info', 'i', html`
        A standard change or standard repair under CS-STAN may be embodied without a separate design
        approval, provided the conditions in the CS-STAN issue are met and it is recorded.
      `);
    default:
      return callout('info', 'i', html`
        A service letter is advisory. It is recorded so the decision not to act on it is visible later,
        which is usually the question that gets asked.
      `);
  }
}

/** What a check result carries over when it is recorded. */
export interface DirectivePrefill {
  authority?: string;
  kind?: string;
  reference?: string;
  revision?: string | null;
  subject?: string;
  issuedAt?: string | null;
  sourceUrl?: string | null;
  matchedOn?: string;
}

export function addDirective(
  ctx: DirectivesContext,
  reload: () => void,
  prefill?: DirectivePrefill,
): void {
  let kind = prefill?.kind ?? 'AD';
  let recurring = false;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Record a directive',
    sub: `${ctx.registration} — currently at ${fmtHours(ctx.tsnHours)} h`,
    primary: 'Record and assess',

    body: () => html`
      <div class="fset">
        <div class="lg">The directive</div>
        <div class="frow3">
          ${field('Issued by', select('authority', AUTHORITIES.map(([v, l]) => opt(v, l, v === prefill?.authority)).join('')))}
          ${field('Kind', select('kind', KINDS.map(([v, l]) => opt(v, l, v === kind)).join(''), 'data-kind'))}
          ${field('Revision', input('revision', prefill?.revision ?? '', 'placeholder="R1" maxlength="16" style="font-family:var(--mono)"'),
            'Leave blank if it is the original issue')}
        </div>
        ${field('Reference', input('reference', prefill?.reference ?? '', 'placeholder="2024-0118" maxlength="60" style="font-family:var(--mono)"'),
          'As published — a revision of the same reference is recorded separately')}
        ${field('Subject', input('subject', prefill?.subject ?? '', 'placeholder="Fuel selector valve — inspection and replacement" maxlength="400"'))}
        ${prefill
          ? callout('info', 'i', html`
              Carried over from the published register — check it against the document before recording it.
              Whether it <b>applies to this aircraft</b> is still your assessment: a keyword search finds
              candidates, not applicability.
            `)
          : ''}
        ${kindNote(kind)}
        <div class="frow">
          ${field('Issued on', input('issuedAt', prefill?.issuedAt ?? '', 'type="date"'))}
          ${field('Effective from', input('effectiveAt', '', 'type="date"'),
            'The date compliance starts being counted from, where it differs')}
        </div>
        ${field('Published at', input('sourceUrl', prefill?.sourceUrl ?? '', 'placeholder="https://ad.easa.europa.eu/ad/2024-0118" maxlength="600"'),
          'The source an auditor can check it against')}
      </div>

      <div class="fset">
        <div class="lg">Initial compliance</div>
        <div class="frow3">
          ${field('Within (flight hours)', input('complyWithinHours', '', 'placeholder="25" inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Within (days)', input('complyWithinDays', '', 'placeholder="90" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
          ${field('Or before', input('complyBefore', '', 'type="date"'))}
        </div>
        ${callout('caution', '!', html`
          The window the directive itself states, counted from the effective date. This is what decides
          when it first comes due — <b>not</b> the repeat interval. An AD saying "within 25 flight hours,
          then every 100" is due in 25.
          <br><br>
          Leave all three empty and the initial compliance is treated as <b>due now</b>, which is the
          honest reading of a mandatory directive with no stated grace.
        `)}
      </div>

      <div class="fset">
        <div class="lg">Recurrence</div>
        <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
          <input type="checkbox" name="isRecurring" data-recurring ${recurring ? 'checked' : ''} style="margin-top:3px">
          <span>This directive <b>repeats</b> — the required action has to be done again at an interval.</span>
        </label>
        ${recurring
          ? html`
              <div class="frow">
                ${field('Every (hours)', input('recurringHours', '', 'placeholder="100" inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
                ${field('Every (months)', input('recurringMonths', '', 'placeholder="12" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
              </div>
              ${callout('info', 'i', html`
                Give whichever the directive states, or both — with both, whichever falls first drives it.
                A repeating directive is added to the maintenance programme as a task, so it appears in
                the forecast and can be picked into a work pack like anything else.
              `)}
              <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
                <input type="checkbox" name="terminatingActionAvailable" style="margin-top:3px">
                <span>
                  The directive offers a <b>terminating action</b> — a modification that ends the repeat
                  once embodied.
                </span>
              </label>
            `
          : ''}
      </div>

      <div class="fset">
        <div class="lg">Applicability to ${esc(ctx.registration)}</div>
        ${field('Why it applies', input('applicabilityNote', '', 'placeholder="Serial number falls within the effectivity in paragraph 3; engine type matches." maxlength="400"'),
          'Recorded against the assessment — this is what is asked for at a review')}
        ${callout('info', 'i', html`
          Recording it here assesses it as <b>applicable to ${esc(ctx.registration)}</b>. A directive that
          affects several aircraft is recorded once per aircraft, so each carries its own compliance and
          its own due state.
        `)}
      </div>
    `,

    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-kind]')?.addEventListener('change', (e) => {
        kind = (e.target as HTMLSelectElement).value;
        refresh();
      });
      root.querySelector<HTMLInputElement>('[data-recurring]')?.addEventListener('change', (e) => {
        recurring = (e.target as HTMLInputElement).checked;
        refresh();
      });
    },

    collect: (root) => {
      const reference = val(root, 'reference').trim();
      if (!reference) return { error: 'A reference is required — it is how the directive is identified.' };
      if (!val(root, 'subject').trim()) {
        return { error: 'A subject is required. "AD 2024-0118" on its own tells the next person nothing.' };
      }

      const hours = numVal(root, 'recurringHours');
      const months = numVal(root, 'recurringMonths');
      if (recurring && !hours && !months) {
        return {
          error: 'A repeating directive needs its interval — hours, months, or both. Without one it never comes due again.',
        };
      }

      const isChecked = (name: string) =>
        root.querySelector<HTMLInputElement>(`[name="${name}"]`)?.checked ?? false;

      return {
        authority: val(root, 'authority'),
        kind: val(root, 'kind'),
        reference,
        revision: val(root, 'revision').trim() || undefined,
        subject: val(root, 'subject').trim(),
        issuedAt: val(root, 'issuedAt') || undefined,
        effectiveAt: val(root, 'effectiveAt') || undefined,
        complyWithinHours: numVal(root, 'complyWithinHours'),
        complyWithinDays: numVal(root, 'complyWithinDays'),
        complyBefore: val(root, 'complyBefore') || undefined,
        sourceUrl: val(root, 'sourceUrl').trim() || undefined,
        isRecurring: recurring,
        recurringHours: recurring ? hours : undefined,
        recurringMonths: recurring ? months : undefined,
        terminatingActionAvailable: recurring && isChecked('terminatingActionAvailable'),
        applicabilityNote: val(root, 'applicabilityNote').trim() || undefined,
      };
    },

    submit: (payload) => api.directives.create(ctx.aircraftId, payload),
    done: (r) =>
      r.derivedTaskCreated
        ? `${r.reference} recorded against ${r.registration} — now in the due list`
        : `${r.reference} recorded against ${r.registration}`,
    onDone: reload,
  });
}
